import { useState, useSyncExternalStore } from 'react';
import { NavLink, useLocation } from 'react-router';

import { Icon } from '@/components/ui';
import { visibleSections, type NavItem } from '@/components/layout/nav-items';
import { useSession } from '@/features/auth/api/auth.api';
import { cn } from '@/lib/cn';
import { useUiStore } from '@/stores/ui.store';

/** Tailwind's `lg`. The width at which labels and content both have room. */
const ROOM_FOR_LABELS = '(min-width: 64rem)';

function subscribeToWidth(onChange: () => void): () => void {
  const query = window.matchMedia(ROOM_FOR_LABELS);

  query.addEventListener('change', onChange);

  return () => {
    query.removeEventListener('change', onChange);
  };
}

/**
 * Vertical navigation down the left.
 *
 * Desktop only: below `md` it is replaced by the off-canvas `MobileNav`, which
 * both layout modes share.
 *
 * Between `md` and `lg` it shows as icons only, whatever the stored
 * preference, and that is the fix for the worst breakpoint in the app rather
 * than a saving of space. Tables choose their columns from the viewport -
 * `sm:`, `md:`, `lg:` - but they are laid out in what is left after this
 * column. At exactly 768px the viewport said "md", so `/jobs` and
 * `/settings/services` laid out their md column sets into the 470px the
 * 192px label rail left them, overflowed by 120px and more, and "Al Noor
 * Trading LLC" wrapped to three lines in a 60px column. At 44px the two
 * numbers agree again: 700px of content for a breakpoint that promised 768.
 *
 * Hiding more columns instead would have made a tablet show less than a
 * phone, and container queries would mean every table in the app knowing
 * about its container - which is a change to the table primitives, not to
 * the navigation that caused this. The preference is not overwritten, only
 * ignored while it cannot be honoured, so widening the window brings the
 * labels straight back.
 */
export function Sidebar() {
  const roomForLabels = useSyncExternalStore(
    subscribeToWidth,
    () => window.matchMedia(ROOM_FOR_LABELS).matches,
  );

  const preferCollapsed = useUiStore((state) => state.sidebarCollapsed);
  const toggleSidebar = useUiStore((state) => state.toggleSidebar);
  const { data: user } = useSession();

  const collapsed = preferCollapsed || !roomForLabels;

  const sections = visibleSections(user);

  return (
    <aside
      className={cn(
        'nav-chrome-vertical hidden shrink-0 flex-col overflow-hidden border-r md:flex',
        /* Width carries the movement; `overflow-hidden` lets the labels be
           clipped by it rather than wrapping into two lines on the way. */
        'transition-[width] duration-panel ease-enter',
        collapsed ? 'w-11' : 'w-48',
      )}
    >
      {/* The collapse control sits at the top, visible without scrolling, and
          on the thing it actually collapses - and is absent where there is
          nothing to collapse, rather than sitting there doing nothing. */}
      {roomForLabels && (
        <div
          className={cn(
            'flex h-8 items-center border-b border-nav-border px-1.5',
            collapsed ? 'justify-center' : 'justify-between',
          )}
        >
          {/* Kept mounted and faded rather than removed: a label that vanishes
            on the first frame makes the width look like it is chasing it. */}
          <span
            className={cn(
              'overflow-hidden text-2xs font-bold tracking-wide whitespace-nowrap text-nav-section uppercase',
              'transition-[opacity,max-width] duration-panel ease-enter',
              collapsed ? 'max-w-0 opacity-0' : 'max-w-24 opacity-100',
            )}
          >
            Menu
          </span>

          <button
            type="button"
            onClick={toggleSidebar}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
            className="nav-item shrink-0 rounded-sm p-1 transition-colors"
          >
            {/* One icon rotated rather than two swapped, so the arrow turns to
              face the other way instead of blinking into a different glyph. */}
            <Icon
              name="chevron-left"
              size={14}
              className={cn(
                'transition-transform duration-panel ease-enter',
                collapsed && 'rotate-180',
              )}
            />
          </button>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto py-1.5" aria-label="Main">
        {sections.map((section, index) => (
          <div key={section.title ?? `section-${index}`} className={index > 0 ? 'mt-3' : ''}>
            {section.title && (
              <p
                className={cn(
                  'mb-0.5 overflow-hidden px-2 text-2xs font-bold tracking-wide whitespace-nowrap text-nav-section uppercase',
                  'transition-[opacity,height,margin] duration-quick ease-enter',
                  collapsed ? 'mb-0 h-0 opacity-0' : 'h-3.5 opacity-100',
                )}
              >
                {section.title}
              </p>
            )}

            <ul>
              {section.items.map((item) =>
                item.children ? (
                  <SidebarGroup key={item.label} item={item} collapsed={collapsed} />
                ) : (
                  <li key={item.to}>
                    <SidebarLink item={item} collapsed={collapsed} />
                  </li>
                ),
              )}
            </ul>
          </div>
        ))}
      </nav>

      <div
        className={cn(
          'overflow-hidden border-t border-nav-border px-2 text-2xs whitespace-nowrap text-nav-section',
          'transition-[opacity,height,padding] duration-panel ease-enter',
          collapsed ? 'h-0 border-t-0 py-0 opacity-0' : 'h-5 py-1 opacity-100',
        )}
      >
        v{__APP_VERSION__}
      </div>
    </aside>
  );
}

/** One destination. Shared by the top level and by the items inside a group. */
function SidebarLink({
  item,
  collapsed,
  nested = false,
}: {
  item: NavItem;
  collapsed: boolean;
  nested?: boolean;
}) {
  return (
    <NavLink
      to={item.to ?? '/'}
      end={item.end}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        cn(
          'nav-item flex items-center gap-2 border-l-2 px-2 py-1.5 text-xs',
          'transition-[color,background-color,border-color,padding] duration-quick',
          collapsed && 'justify-center px-0',
          /* Indented rather than given its own rule: one border down the left
             already marks the active row, and a second line would compete. */
          nested && !collapsed && 'pl-7',
          isActive ? 'nav-item-active border-primary-active font-bold' : 'border-transparent',
        )
      }
    >
      <Icon name={item.icon} size={13} className="shrink-0" />
      <span
        className={cn(
          'overflow-hidden whitespace-nowrap',
          'transition-[opacity,max-width] duration-panel ease-enter',
          collapsed ? 'max-w-0 opacity-0' : 'max-w-32 opacity-100',
        )}
      >
        {item.label}
      </span>
    </NavLink>
  );
}

/**
 * A heading that opens onto its own items.
 *
 * Starts open when the current page is one of its children, so arriving at a
 * settings page by link or by refresh shows where you are rather than a shut
 * drawer. Collapsing the whole sidebar flattens the group to its icons: there
 * is no room for a disclosure arrow at 44px wide, and the children stay
 * reachable.
 */
function SidebarGroup({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const { pathname } = useLocation();
  const holdsCurrent = (item.children ?? []).some((child) => child.to === pathname);
  const [open, setOpen] = useState(holdsCurrent);

  /* Navigating into the group from elsewhere should open it, without closing
     one the user opened by hand. */
  const [lastHeld, setLastHeld] = useState(holdsCurrent);
  if (holdsCurrent !== lastHeld) {
    setLastHeld(holdsCurrent);
    if (holdsCurrent) {
      setOpen(true);
    }
  }

  if (collapsed) {
    return (
      <>
        {(item.children ?? []).map((child) => (
          <li key={child.to}>
            <SidebarLink item={child} collapsed />
          </li>
        ))}
      </>
    );
  }

  return (
    <li>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => {
          setOpen((current) => !current);
        }}
        className={cn(
          'nav-item flex w-full items-center gap-2 border-l-2 border-transparent px-2 py-1.5 text-xs',
          'transition-colors duration-quick',
          holdsCurrent && 'font-bold',
        )}
      >
        <Icon name={item.icon} size={13} className="shrink-0" />
        <span className="flex-1 overflow-hidden text-left whitespace-nowrap">{item.label}</span>
        <Icon
          name="chevron-down"
          size={12}
          className={cn('shrink-0 transition-transform duration-quick', open && 'rotate-180')}
        />
      </button>

      {open && (
        <ul>
          {(item.children ?? []).map((child) => (
            <li key={child.to}>
              <SidebarLink item={child} collapsed={false} nested />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
